import { useEffect, useState } from 'react';
import Tracker from 'utils/Tracking';
import PhoneForm from 'containers/Login/components/PhoneForm';
import VerificationCodeForm from 'containers/Login/components/VerificationCodeForm';
import LoginContainer from 'containers/Login/components/LoginContainer';
import { useTranslation } from 'react-i18next';
import useSourceTracker from 'containers/Login/utils/useSourceTracker';
import MediquoPatientBadge from 'components/icons/MediquoPatientBadge';
import LogoMediquoPro from 'components/icons/LogoMediquoPro';
import ChannelForm from 'containers/Login/components/ChannelForm';

type Phone = {
  prefix: string;
  number: string;
};

const SmsSignIn = () => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState<Phone | null>(null);
  const [screen, setScreen] = useState<'init' | 'channel' | 'code'>('init');

  useSourceTracker();

  useEffect(() => {
    Tracker.pageView('login view');
  }, []);

  const resetPhone = () => {
    setPhone(null);
    setScreen('init');
  };

  const performPhone = ({
    phone,
    prefix,
  }: {
    phone: string;
    prefix: string;
  }) => {
    setPhone({ prefix, number: phone });
    setScreen('channel');
  };

  switch (screen) {
    case 'init':
      return (
        <LoginContainer>
          <div className="flex flex-col items-center justify-center gap-4 lg:items-stretch lg:flex-row">
            <LoginContainer.Panel>
              <LogoMediquoPro className="my-5 sm:hidden" />
              <LoginContainer.Title>
                {t('login__send_code_form_title')}
              </LoginContainer.Title>
              <LoginContainer.Description className="hidden sm:block">
                {t('login__send_code_form_text')}
              </LoginContainer.Description>
              <PhoneForm onSend={performPhone} />
            </LoginContainer.Panel>
          </div>
          <hr className="mx-6 my-5 md:mx-auto md:max-w-xl border-separators lg:hidden" />
          <div className="flex flex-col items-center justify-center lg:hidden">
            <div className="mb-2 text-base font-medium text-dark">
              {t('login__responsive_patient_title')}
            </div>
            <div className="w-full px-6 md:max-w-xl">
              <a
                href="https://eat.emmasolutions.net/?entw=dec1fe43825d340d60a736e9ca73fade"
                className="flex items-center p-4 rounded-lg cursor-pointer bg-background md:bg-white"
              >
                <MediquoPatientBadge className="mr-4" />
                <div className="flex flex-col text-xs text-dark">
                  <div className="font-medium tracking-widest uppercase">
                    Mediquo
                  </div>
                  <div>{t('login__responsive_patient_text')}</div>
                </div>
              </a>
            </div>
          </div>
        </LoginContainer>
      );
    case 'channel':
      return (
        <LoginContainer>
          <LoginContainer.Panel className="m-auto">
            <LoginContainer.Title>
              {t('login__choose_channel_form_title')}
            </LoginContainer.Title>
            <LoginContainer.Description>
              {t('login__choose_channel_form_text')}
            </LoginContainer.Description>
            <ChannelForm
              phone={phone}
              onSend={() => setScreen('code')}
              onBack={resetPhone}
            />
          </LoginContainer.Panel>
        </LoginContainer>
      );
    case 'code':
      return (
        <LoginContainer>
          <LoginContainer.Panel className="m-auto">
            <LoginContainer.Title>
              {t('login__check_code_form_title')}
            </LoginContainer.Title>
            <LoginContainer.Description>
              {t('login__check_code_form_text')}
            </LoginContainer.Description>
            <VerificationCodeForm
              prefix={phone!.prefix}
              phone={phone!.number}
              onClose={() => setScreen('channel')}
            />
          </LoginContainer.Panel>
        </LoginContainer>
      );
  }
};

export default SmsSignIn;
